import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { isCompareGraphOverlay } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { saveView } from 'components/Graph/commands/saveView';
import { useLocale } from 'containers/App/LocaleContext';
import { flow } from 'fp-ts/lib/function';
import { useState } from 'react';
import type { FC } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useGraphContextSelectors } from '../GraphContext';
import { CreateIcon } from '../GraphSidePanel/icons/CreateIcon';
import { GraphToolbarButton } from '../GraphToolbar/GraphToolbarButton';
import { SaveViewForm, type SaveViewFormValues } from './SaveViewForm';

interface Props {
  disabled: boolean;
}

export const SaveViewAs: FC<Props> = (props) => {
  const [loadingState, setLoadingState] = useLoadingState();
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    compareActive: flow(prop('overlay'), isCompareGraphOverlay),
  });
  const msg = useLocale();
  const [open, setOpen] = useState<boolean>(false);
  const disabled = props.disabled || ctxProps.compareActive;
  const { showNotification } = useNotifications();

  const toggleOpen = () => {
    if (loadingState !== 'LOADING') {
      setOpen(!open);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async (valuesToSave: SaveViewFormValues) => {
    if (loadingState !== 'LOADING') {
      try {
        await withLoadingIndicator(setLoadingState)(async () => {
          handleClose();
          await ctxProps.dispatch(saveView, valuesToSave);
        });
      } catch {
        showNotification({
          type: 'danger',
          message: msg.canvasGraph.commands.saveView.cannotSaveView,
        });
      }
    }
  };

  const wrapperRef = useClickAway<HTMLDivElement>(handleClose);

  return (
    <div className='relative' ref={wrapperRef}>
      {open && <SaveViewForm onClose={handleClose} onSubmit={handleSubmit} />}
      <GraphToolbarButton
        disabled={disabled}
        disabledTitle={
          props.disabled
            ? msg.canvasGraph.graphToolbar.buttons.selectDataToDisplay
            : msg.canvasGraph.graphToolbar.buttons.saveViewAsDisabledCompare
        }
        onClick={toggleOpen}
        title={msg.canvasGraph.graphToolbar.buttons.saveViewAs}
      >
        {loadingState === 'LOADING' ? (
          <div className='graph-save-view__spinner-wrapper'>
            <i className='fa-regular fa-spinner fa-spin fa-fw' />
          </div>
        ) : (
          <CreateIcon
            title={msg.canvasGraph.graphToolbar.buttons.saveViewAs}
            height={16}
            width={16}
          />
        )}
      </GraphToolbarButton>
    </div>
  );
};
